import { useTranslation } from 'next-i18next/pages'
import { useForm } from 'react-hook-form'
import { useStoreDispatch, useStoreSelector } from 'hooks/store'
import styles from './ChangeProductDrawer.module.scss'
import { getUsableProducts } from 'utils/products'
import { Fragment, useEffect, useState } from 'react'
import { requestAndSavePropositions } from 'features/simulation/utils'
import { useRouter } from 'next/router'
import { Language } from 'types/language'
import { useGetCmsProductsQuery } from 'store/api/cms-api'
import PriceDisplay from 'features/simulation/components/proposition-card/price-display/PriceDisplay'
import BlocksRenderer from 'components/blocks-renderer/BlocksRenderer'
import { InputGroupKeys } from 'store/customer/enums'
import { Button, Drawer, DrawerProps } from '@boltenergy-be/design-system'
import { selectProposition } from 'store/customer/slice'
import ExpandableSelect from 'components/expandable-select/ExpandableSelect'
import { Product, REGIONS } from '@boltenergy-be/shared'

const ChangeProductDrawer = (props: DrawerProps) => {
  // Redux
  const { propositions, inputs, flows, fixedPrices } = useStoreSelector((state) => state.customer)
  const { isLoading: simulationLoading } = flows.simulation
  const { forcedAmount } = inputs[InputGroupKeys.SIMULATION]
  const dispatch = useStoreDispatch()

  // router
  const { locale } = useRouter()

  // Redux queries
  const { data: cmsProducts, isLoading: contentLoading } = useGetCmsProductsQuery({
    language: locale as Language
  })

  // i18n
  const { t } = useTranslation(['registration', 'validation', 'common'])

  // React hook form
  const {
    register,
    watch,
    handleSubmit,
    formState: { errors }
  } = useForm<{ product: Product }>({
    defaultValues: {
      product: propositions.selectedProposition
    },
    mode: 'onChange'
  })
  const watchProduct = watch('product')

  // Local state
  const [refetchedProducts, setRefetchedProducts] = useState<boolean>(false)
  const [showDetailsFor, setShowDetailsFor] = useState<Product | null>(propositions.selectedProposition)

  // constants
  const isLoading = simulationLoading || contentLoading
  const usableProducts = getUsableProducts({ region: inputs.simulation.region })
  const missingUsableProductSimulation = usableProducts.some((product) => !propositions?.simulatedPropositions?.[product]?.price)

  useEffect(() => {
    if (!refetchedProducts && missingUsableProductSimulation) {
      requestAndSavePropositions({ onSuccess: () => setRefetchedProducts(true), products: usableProducts })
    }
  }, [])

  /**
   * Handles the form submit
   * @param {{product: Product}} data
   */
  const changeProduct = (data: { product: Product }) => {
    dispatch(selectProposition({ productType: data.product }))
    props.onRequestClose()
  }

  return (
    <Drawer {...props} title={t('billing.drawers.changeProduct.title')}>
      <form onSubmit={handleSubmit(changeProduct)} className={styles.form}>
        <fieldset>
          <ul className={styles.list}>
            {isLoading && usableProducts.map((product) => <li key={product} className={styles.skeleton} />)}
            {!isLoading &&
              (!missingUsableProductSimulation || refetchedProducts) &&
              usableProducts
                .filter((p) => !!propositions.simulatedPropositions[p]?.price)
                .map((product) => (
                  <Fragment key={product}>
                    <ExpandableSelect
                      input={{ value: product, ...register('product', { required: true }) }}
                      title={`Bolt ${cmsProducts?.[product]?.name}`}
                      isOpen={showDetailsFor === product}
                      handleOpen={() => setShowDetailsFor(product)}
                      handleClose={() => setShowDetailsFor(null)}
                      secondLine={
                        !fixedPrices &&
                        !forcedAmount && (
                          <PriceDisplay
                            proposition={propositions.simulatedPropositions[product]}
                            label={`${t('common:perMonth', '/maand')} (${
                              inputs.personalData.isCompany ? t('common:exclVat', 'excl. BTW') : t('common:inclVat', 'incl. BTW')
                            })`}
                          />
                        )
                      }
                    >
                      {(cmsProducts[product].alternativePerks || cmsProducts[product].perks) && (
                        <>
                          {inputs.simulation.region !== REGIONS.flanders && cmsProducts?.[product]?.alternativePerks ? (
                            <BlocksRenderer content={cmsProducts[product].alternativePerks} />
                          ) : (
                            <BlocksRenderer content={cmsProducts[product].perks} />
                          )}
                        </>
                      )}
                    </ExpandableSelect>
                  </Fragment>
                ))}
          </ul>

          {errors?.product?.type === 'required' && <p className="is-error-text">{t('validation:required')}</p>}
        </fieldset>

        <div className={styles['btn-group']}>
          <Button isFullwidth disabled={isLoading || !watchProduct}>
            {t('billing.drawers.changeProduct.button')}
          </Button>

          <Button variant="layered" isFullwidth onClick={() => props.onRequestClose()}>
            {t('common:cancel', 'Annuleren')}
          </Button>
        </div>
      </form>
    </Drawer>
  )
}

export default ChangeProductDrawer
